import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useState, type JSX } from 'react';
import { RouterProvider } from 'react-router-dom';

import { ThemeProvider } from '@/components/theme/ThemeProvider';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/lib/auth/AuthContext';
import { createQueryClient } from '@/lib/query/client';
import { appRouter } from '@/routes';

/**
 * App composition root — finalized in plan 03-04.
 *
 * Provider order (outer → inner):
 *   ThemeProvider          ← outermost so the Toaster (which calls `useTheme`) can read theme
 *   QueryClientProvider    ← wraps AuthProvider so future authed queries can read auth state
 *   AuthProvider           ← exposes useAuth() to PrivateRoute and pages
 *   RouterProvider         ← createBrowserRouter table (replaces 03-03's MemoryRouter)
 *   Toaster + Devtools     ← siblings of RouterProvider, share the AuthProvider scope
 */
export default function App(): JSX.Element {
  // Stable QueryClient across renders — survives React StrictMode double-mount in dev.
  const [queryClient] = useState(createQueryClient);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={appRouter} />
          <Toaster richColors closeButton position="top-right" />
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
